import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import PlayerSetup from './components/PlayerSetup'
import PreRaceDashboard from './components/PreRaceDashboard'
import RaceDayDashboard from './components/RaceDayDashboard'
import Results from './components/Results'
import PlayerPage from './components/PlayerPage'
import { BORG_WARNER_TROPHY } from './driverPhotos'
import { CountdownHero } from './components/CountdownTimer'

type Screen = 'welcome' | 'setup' | 'prerace' | 'race' | 'results' | 'player'

interface Assignment { playerName: string; drivers: { number: number; name: string; team?: string }[] }
interface Prizes { first: number; second: number; third: number }

export default function App() {
  const [screen,           setScreen]           = useState<Screen>('welcome')
  const [gameId,           setGameId]           = useState('')
  const [assignments,      setAssignments]      = useState<Assignment[]>([])
  const [prizes,           setPrizes]           = useState<Prizes>({ first: 50, second: 30, third: 20 })
  const [finalLeaderboard, setFinalLeaderboard] = useState<any[]>([])
  const [selectedPlayer,   setSelectedPlayer]   = useState(0)
  const [raceStarted,      setRaceStarted]      = useState(false)

  // Handle email invite links: ?game=GAMEID&player=INDEX
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const gameParam  = params.get('game')
    const playerParam = params.get('player')
    if (!gameParam || playerParam === null) return

    fetch('/api/players/assignments')
      .then(r => r.json())
      .then(data => {
        if (!data.assignments?.length) return
        setAssignments(data.assignments)
        setPrizes(data.prizes)
        setGameId(gameParam)
        setSelectedPlayer(Math.min(parseInt(playerParam), data.assignments.length - 1))
        setScreen('player')
        // Clean URL without reloading
        window.history.replaceState({}, '', '/')
      })
      .catch(() => {})
  }, [])

  const handleSetupComplete = (gid: string, asn: Assignment[], p: Prizes) => {
    setGameId(gid)
    setAssignments(asn)
    setPrizes(p)
    setScreen('prerace')
  }

  // Called once — tells the backend to start polling and moves to race screen.
  // Subsequent navigations back to the race screen do NOT re-call this.
  const handleStartRace = () => {
    if (!raceStarted) {
      fetch('/api/race/start', { method: 'POST' })
      setRaceStarted(true)
    }
    setScreen('race')
  }

  const handleRaceFinish = (leaderboard: any[]) => {
    setFinalLeaderboard(leaderboard)
    setScreen('results')
  }

  const handleNewGame = () => {
    setGameId('')
    setAssignments([])
    setFinalLeaderboard([])
    setRaceStarted(false)
    setScreen('welcome')
  }

  const goToRace    = () => setScreen('race')
  const goToRoster  = () => setScreen('prerace')

  return (
    <div className="app">
      {/* ── Header ── */}
      <header className="app-header">
        <div className="header-logo">🏎️</div>
        <div className="header-title">
          <h1>Race Day Pool</h1>
          <p>Indianapolis Motor Speedway · May 24, 2026</p>
        </div>
        {screen !== 'welcome' && screen !== 'setup' && (
          <nav className="header-nav">
            {/* Roster — always available once players are set up */}
            {assignments.length > 0 && (
              screen === 'prerace'
                ? <button className="nav-btn active">📋 Roster</button>
                : <button className="nav-btn" onClick={goToRoster}>📋 Roster</button>
            )}

            {/* Live Race — only available once started; cannot be re-started */}
            {raceStarted && (
              screen === 'race'
                ? <button className="nav-btn active">🔴 Live Race</button>
                : <button className="nav-btn" onClick={goToRace}>🔴 Live Race</button>
            )}

            {screen === 'results' && (
              <button className="nav-btn active">🏆 Results</button>
            )}

            {/* New Game is the only way to end a race in progress */}
            <button className="nav-btn" onClick={handleNewGame}>↩ New Game</button>
          </nav>
        )}
      </header>

      {/* ── Main content ── */}
      <main className="app-main">

        {screen === 'welcome' && (
          <>
            <div className="welcome-screen">
              <img
                className="welcome-hero-img"
                src="https://upload.wikimedia.org/wikipedia/commons/c/ca/2020-IndyCar-HarvestGP-Race2.jpg"
                alt="IndyCar race"
              />
              <div className="welcome-hero-overlay" />

              {/* Trophy — right-side background element */}
              <img
                src={BORG_WARNER_TROPHY}
                alt=""
                aria-hidden="true"
                style={{
                  position: 'absolute', right: '0', top: '0',
                  height: '100%', width: 'auto',
                  objectFit: 'cover', objectPosition: 'top center',
                  zIndex: 1,
                  maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.6) 28%, rgba(0,0,0,0.85) 100%)',
                  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.6) 28%, rgba(0,0,0,0.85) 100%)',
                  filter: 'brightness(0.55) saturate(0.75)',
                  pointerEvents: 'none',
                }}
              />

              <div className="welcome-content">
                <div className="welcome-logo">🏁</div>
                <div className="welcome-title">Race Day Pool</div>
                <div className="welcome-sub">Race Day Pool Manager · Indianapolis Motor Speedway · May 24, 2026</div>
                <div style={{ margin: '1.2rem 0' }}>
                  <CountdownHero />
                </div>
                <div className="welcome-actions">
                  <Link to="/login" className="btn btn-primary">
                    🏎️ Get Started · $3.99
                  </Link>
                  {assignments.length > 0 && (
                    <button className="btn btn-secondary" onClick={() => setScreen('prerace')}>
                      📋 Resume Game
                    </button>
                  )}
                  <Link to="/login" className="btn btn-secondary">
                    🔐 Sign In
                  </Link>
                </div>
                <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)', maxWidth: '420px' }}>
                  One-time $3.99 · Unlimited games · 33 drivers · Live race tracking
                </div>
              </div>
            </div>

            {/* ── Features Section ── */}
            <div className="features-section">
              <div className="features-header">
                <h2 className="features-title">Everything you need for race day</h2>
                <p className="features-subtitle">
                  Set up in minutes. Run your group tracker from any device. Never miss a position change.
                </p>
              </div>

              <div className="features-grid">
                {[
                  { icon: '🎲', title: 'Random Draft, Zero Drama',
                    desc: 'All 33 Indianapolis 500 starters are randomly dealt to your players — no trades, no picks, no disputes. Pure racing luck decides everything.' },
                  { icon: '🔴', title: 'Live Race Tracking',
                    desc: 'Real-time leaderboard powered by IndyCar data. Positions update every 10 seconds so you\'re never behind the action on race day.' },
                  { icon: '💰', title: 'Customizable Score Tracker',
                    desc: 'Set your own 1st, 2nd, and 3rd place amounts. The app tracks who\'s leading in real time as positions change throughout the race.' },
                  { icon: '📱', title: 'Shareable Player Links',
                    desc: 'Each player gets a personal link to follow only their drivers — perfect for groups watching from different living rooms or sports bars.' },
                  { icon: '🚨', title: 'DNF Tracker',
                    desc: 'Crashes and retirements are flagged instantly. Always know exactly which drivers are still running and who\'s been eliminated.' },
                  { icon: '🏁', title: 'Instant Final Standings',
                    desc: 'The moment the checkered flag drops, final standings are calculated and displayed automatically. No spreadsheets, no arguments, no math.' },
                  { icon: '🔐', title: 'Save Your Games',
                    desc: 'Create an account and every game is saved. Run multiple games at once or review past race results any time.' },
                  { icon: '⚡', title: 'One-Time Fee — No Subscriptions',
                    desc: 'Pay $3.99 once for full software access and run as many games as you want for the entire 2026 Indianapolis 500 season. No recurring charges, ever.' },
                ].map(f => (
                  <div className="feature-card" key={f.title}>
                    <div className="feature-icon">{f.icon}</div>
                    <div className="feature-title">{f.title}</div>
                    <div className="feature-desc">{f.desc}</div>
                  </div>
                ))}
              </div>

              {/* Bottom CTA */}
              <div className="features-cta">
                <div className="features-cta-text">
                  Ready to track your 2026 Indy 500 group?
                </div>
                <Link to="/login" className="btn btn-primary" style={{ fontSize: '1rem', padding: '0.9rem 2.4rem' }}>
                  🏎️ Create Account · $3.99
                </Link>
                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.5rem' }}>
                  One-time payment · Secured by Stripe · Instant access
                </div>
              </div>
            </div>
          </>
        )}

        {screen === 'setup' && (
          <PlayerSetup onSetupComplete={handleSetupComplete} />
        )}

        {screen === 'prerace' && assignments.length > 0 && (
          <PreRaceDashboard
            assignments={assignments}
            prizes={prizes}
            gameId={gameId}
            raceStarted={raceStarted}
            onStartRace={handleStartRace}
            onViewPlayer={(idx) => { setSelectedPlayer(idx); setScreen('player') }}
          />
        )}

        {screen === 'race' && assignments.length > 0 && (
          <RaceDayDashboard
            assignments={assignments}
            prizes={prizes}
            onRaceFinish={handleRaceFinish}
            onViewPlayer={(idx) => { setSelectedPlayer(idx); setScreen('player') }}
          />
        )}

        {screen === 'player' && assignments.length > 0 && (
          <PlayerPage
            playerIndex={selectedPlayer}
            assignments={assignments}
            prizes={prizes}
            onBack={goToRace}
          />
        )}

        {screen === 'results' && (
          <Results
            assignments={assignments}
            prizes={prizes}
            finalLeaderboard={finalLeaderboard}
            onNewGame={handleNewGame}
          />
        )}

      </main>
    </div>
  )
}
